<template>
  <div class="chart-block">
    <vue-echarts
      class="w-full"
      :option="option"
      :style="{ height: `${height}px` }"
    />
    <div class="flex justify-end">
      <el-button
        link
        size="small"
        data-testid="ai-chat-chart-table-toggle-button"
        @click="isTableVisible = !isTableVisible"
      >
        {{
          isTableVisible
            ? t('aichat.main.blocks.chart.hide_table')
            : t('aichat.main.blocks.chart.show_table')
        }}
      </el-button>
    </div>
    <el-table
      v-if="isTableVisible"
      :data="tableRows"
      size="small"
      max-height="240"
      class="chart-table"
    >
      <el-table-column
        prop="label"
        :label="t('aichat.main.blocks.chart.bucket')"
        min-width="90"
      />
      <el-table-column
        prop="current"
        :label="seriesName(series[0])"
        align="right"
      >
        <template #default="{ row }">
          <span data-testid="ai-chat-chart-table-current">{{
            row.current
          }}</span>
        </template>
      </el-table-column>
      <el-table-column
        v-if="hasPrevious"
        prop="previous"
        :label="seriesName(series[1])"
        align="right"
      />
    </el-table>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { VueEcharts } from 'vue3-echarts'
import type { EChartsOption } from 'echarts'
import type { ChartBlock, ChartPeriod, ChartSeries } from '../../types'
import { useFormatValue } from './format'

interface ChartTableRow {
  label: string
  current: string
  previous: string
}

/* bar or line chart of up to two periods, with the same numbers as a table for screen readers and print */
const props = withDefaults(
  defineProps<{
    block: ChartBlock
    height?: number
  }>(),
  { height: 240 }
)

const { t } = useI18n()
const formatValue = useFormatValue()

const isTableVisible = ref(false)

/* ECharts paints on a canvas, which cannot resolve var(), so the brand's value is read when the option is built */
const cssVar = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim()

const labels = computed(() => props.block.graph.axisLabels || [])
const series = computed(() => props.block.graph.series.slice(0, 2))
const hasPrevious = computed(() => series.value.length > 1)
/* any non-zero bucket is data, so a period of only refunds still renders */
const hasData = computed(() =>
  series.value.some((item) => item.data.some((value) => value !== 0))
)
const unit = computed(() => series.value[0]?.unit)

const periodNames = computed<Record<ChartPeriod, string>>(() => ({
  current: t('aichat.main.blocks.chart.current'),
  previous: t('aichat.main.blocks.chart.previous')
}))

/* the series' own label, e.g. 'Mitte', else the period it stands for */
const seriesName = (item?: ChartSeries): string =>
  item?.label || (item ? periodNames.value[item.period] : '')

const option = computed<EChartsOption>(() => {
  const { chartType } = props.block
  const drawnSeries = hasData.value ? series.value : []
  /* the home revenue widget's colours: current period, previous period */
  const chartColors = [
    cssVar('--el-color-primary'),
    cssVar('--el-color-primary-light-7')
  ]
  const axisColor = cssVar('--el-text-color-secondary')

  return {
    animation: false,
    color: chartColors,
    title: {
      show: !hasData.value,
      text: t('aichat.main.blocks.chart.no_data'),
      left: 'center',
      top: 'center',
      textStyle: {
        color: cssVar('--el-text-color-placeholder'),
        fontSize: 14,
        fontWeight: 'normal'
      }
    },
    legend: {
      show: drawnSeries.length > 1,
      top: 0,
      right: 0,
      icon: 'roundRect',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: axisColor, fontSize: 12 }
    },
    grid: {
      left: 0,
      right: 10,
      top: drawnSeries.length > 1 ? 30 : 10,
      bottom: 0,
      containLabel: true
    },
    xAxis: {
      show: hasData.value,
      type: 'category',
      data: labels.value,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: axisColor, hideOverlap: true }
    },
    yAxis: {
      show: hasData.value,
      type: 'value',
      splitNumber: 4,
      splitLine: { lineStyle: { color: cssVar('--el-border-color-main') } },
      axisLabel: {
        color: axisColor,
        /* whole currency units on the axis, e.g. '€400' instead of '€400.00' */
        formatter: (value: number) => formatValue(value, unit.value, 0)
      }
    },
    tooltip: {
      trigger: chartType === 'line' ? 'axis' : 'item',
      transitionDuration: 0,
      /* the default tooltip escapes the bucket and series names; only the value is formatted here */
      valueFormatter: (value) =>
        typeof value === 'number' ? formatValue(value, unit.value) : ''
    },
    series: drawnSeries.map((item) => ({
      id: item.id ?? item.period,
      name: seriesName(item),
      type: chartType,
      data: item.data,
      barMaxWidth: 24,
      barGap: '12%',
      symbolSize: 8,
      lineStyle: { width: 2 },
      itemStyle: { borderRadius: chartType === 'bar' ? [4, 4, 0, 0] : 0 },
      emphasis: { focus: 'series' }
    }))
  }
})

const tableRows = computed<ChartTableRow[]>(() => {
  const [current, previous] = series.value
  return labels.value.map((label, index) => ({
    label,
    current: formatValue(current?.data[index] ?? 0, unit.value),
    previous: previous
      ? formatValue(previous.data[index] ?? 0, previous.unit)
      : ''
  }))
})
</script>

<style scoped>
.chart-table {
  margin-top: 0.25rem;
  font-size: 0.75rem;
}
</style>
